import { useEffect, useRef, useState } from 'react';
import { Upload, Button, useApp, Image, Space, Tooltip, theme } from '../ui';
import { File, FileText, PlayCircle, Trash2, Upload as UploadIcon } from 'lucide-react';
import type { UploadProps } from '../ui';
import { api } from '../api/client';
import type { UploadCategory } from './FileUploadField';
import { PALETTE } from '../lib/palette';
import { fileLabel } from '../lib/fileName';

// Per-category accept + size cap (mirrors the backend uploads guard).
const ACCEPT: Partial<Record<UploadCategory, string>> = {
  receipt: 'image/*,application/pdf',
  pod: 'image/*,application/pdf',
  evidence: 'image/*,application/pdf,video/*',
  attachment: '*/*',
  'product-video': 'video/mp4,video/quicktime,video/webm',
};
const MAX_MB: Partial<Record<UploadCategory, number>> = { receipt: 10, pod: 10, evidence: 25, attachment: 25, 'product-video': 50 };

const isImg = (u: string) => /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(u);
const isPdf = (u: string) => /\.pdf(\?|$)/i.test(u);
const isVideo = (u: string) => /\.(mp4|mov|m4v|webm|ogv|ogg)(\?|$)/i.test(u);

interface Props {
  /** File URLs — bound via Form.Item value/onChange. */
  value?: string[];
  onChange?: (urls: string[]) => void;
  category: UploadCategory;
  max?: number;
  disabled?: boolean;
  buttonLabel?: string;
}

/**
 * Upload MANY files (images or PDFs) to a category, returning their URLs. The same serialized
 * append queue as MultiImageUploadField keeps parallel selections from clobbering each other.
 */
export function MultiFileUploadField({ value, onChange, category, max = 8, disabled, buttonLabel }: Props) {
  const { message } = useApp();
  const { token } = theme.useToken();
  const [pending, setPending] = useState(0);
  const urls = value ?? [];
  const busy = pending > 0;
  const accept = ACCEPT[category] ?? '*/*';
  const maxMb = MAX_MB[category] ?? 15;

  const urlsRef = useRef<string[]>(urls);
  useEffect(() => { urlsRef.current = value ?? []; }, [value]);
  const queue = useRef<Promise<void>>(Promise.resolve());

  const uploadProps: UploadProps = {
    accept,
    showUploadList: false,
    multiple: true,
    disabled: disabled || busy,
    beforeUpload: (file) => {
      setPending((p) => p + 1);
      queue.current = queue.current.then(async () => {
        try {
          if (urlsRef.current.length >= max) { message.warning(`You can add up to ${max} files`); return; }
          if (file.size > maxMb * 1024 * 1024) { message.error(`"${file.name}" is too large — max ${maxMb} MB`); return; }
          const form = new FormData();
          form.append('file', file);
          const r = await api.post(`/uploads/${category}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
          const url = (r.data?.data ?? r.data)?.url;
          if (!url) throw new Error('Server did not return a URL');
          const next = [...urlsRef.current, url];
          urlsRef.current = next;
          onChange?.(next);
        } catch (e: any) {
          message.error(e.response?.data?.error?.message ?? e.message ?? 'Upload failed');
        } finally {
          setPending((p) => p - 1);
        }
      });
      return Upload.LIST_IGNORE;
    },
  };

  const removeAt = (i: number) => {
    const next = urls.filter((_, idx) => idx !== i);
    urlsRef.current = next;
    onChange?.(next);
  };

  const chip = (u: string, i: number) => {
    // The name the file was uploaded under, not the name it is stored under (see lib/fileName).
    const name = fileLabel(u, { index: i + 1 });
    return (
      <div key={`${u}-${i}`} style={{ position: 'relative', border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, padding: 4, width: 96 }}>
        {isImg(u) ? (
          <Image src={u} width={86} height={70} style={{ objectFit: 'cover', borderRadius: 6 }} preview={{ src: u }} />
        ) : (
          <a href={u} target="_blank" rel="noopener"
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 70, gap: 4, color: token.colorText }}>
            {isPdf(u) ? <FileText size={24} color={PALETTE.danger[600]} /> : isVideo(u) ? <PlayCircle size={24} color={token.colorPrimary} /> : <File size={24} />}
            <span title={name} style={{ fontSize: 10, maxWidth: 84, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          </a>
        )}
        <Tooltip title="Remove">
          <Button size="sm" shape="circle" variant="danger" aria-label="Remove file" icon={<Trash2 size={16} />} onClick={() => removeAt(i)} disabled={disabled}
            style={{ position: 'absolute', top: -8, right: -8 }} />
        </Tooltip>
      </div>
    );
  };

  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      <Space wrap>
        <Upload {...uploadProps}>
          <Button variant="default" icon={<UploadIcon size={16} />} loading={busy} disabled={disabled || urls.length >= max}>{buttonLabel ?? 'Upload files'}</Button>
        </Upload>
        <span style={{ color: token.colorTextTertiary, fontSize: 12 }}>{urls.length}/{max}{category === 'product-video' ? ' · video files' : ' · images or PDF'}</span>
      </Space>
      {urls.length > 0 && <Space wrap size={10}>{urls.map(chip)}</Space>}
    </Space>
  );
}
